import { WeightUnit } from '@shopify-multiseller/graphql/partner/types';
import { centsToDollars } from '@shopify-multiseller/common';

export const formatDate = (
  date: string,
  format: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  },
) => new Date(date).toLocaleDateString('en-US', format);

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

/**
 * "2 days ago" rather than a date, for columns where the question is how stale
 * a row is, not which calendar day it happened on. Anything under a minute is
 * "just now" — the seconds are never the point.
 */
export const formatRelativeTime = (date: string | number | Date, now: Date = new Date()): string => {
  const then = new Date(date).getTime();
  if (Number.isNaN(then)) return '';

  const elapsed = now.getTime() - then;
  // 'always', so a column of these is uniform: "1 week ago" beside "2 weeks
  // ago", rather than "last week" beside "2 weeks ago".
  const formatter = new Intl.RelativeTimeFormat('en-US', { numeric: 'always' });

  for (const [unit, ms] of RELATIVE_UNITS) {
    if (Math.abs(elapsed) >= ms) {
      return formatter.format(-Math.round(elapsed / ms), unit);
    }
  }
  return 'just now';
};

interface CurrencyFormatOptions {
  /** The amount to format */
  amount: number | string;
  /** The currency code (e.g. 'USD', 'EUR') */
  currencyCode?: string;
  /** How to display the currency. Defaults to 'symbol' */
  display?: 'symbol' | 'code' | 'name' | 'none';
}

/**
 * Format a currency amount with options for currency code and display format.
 * (The old bare-amount overload silently formatted everything as USD — it is
 * gone so a missing currency is now a compile error, not a wrong symbol.)
 * @param options Object containing amount, currency code, and display options
 * @returns Formatted currency string
 */
export function formatCurrency(options: CurrencyFormatOptions): string {
  const { amount, currencyCode = 'USD', display = 'symbol' } = options;

  if (display === 'none') {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: display,
  }).format(Number(amount));
}

/**
 * Format an integer-cents amount for display.
 *
 * Monetary values are stored and computed as integer cents (see
 * `packages/common/src/helpers/money.ts`), but `formatCurrency` wraps
 * `Intl.NumberFormat`, which expects dollars — handing it cents renders every
 * figure 100x too large. Every already-cents display site goes through here so
 * the conversion has one definition per app rather than one per call site.
 */
export function formatCents(options: CurrencyFormatOptions): string {
  const { amount, ...rest } = options;
  return formatCurrency({ ...rest, amount: centsToDollars(Number(amount)) });
}

export const formatWeight = (weight: string | number, unit:WeightUnit) => {
  // resolve weight unit string from unit enum by using a switch statement
  let unitString = '';
  switch (unit) {
    case WeightUnit.Grams:
      unitString = 'g';
      break;
    case WeightUnit.Kilograms:
      unitString = 'kg';
      break;
    case WeightUnit.Ounces:
      unitString = 'oz';
      break;
    case WeightUnit.Pounds:
      unitString = 'lb';
      break;
    default:
      unitString = '';
  }
  return `${weight} ${unitString}`;
}

